import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Clock,
  CheckCircle,
  Calendar,
  Send,
  FileText,
  ArrowRight,
  Sparkles,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const stageOf = (p) => p?.approval_stage || p?.status;

const STAGE_META = {
  client_review: { label: 'Awaiting Review', cls: 'status-pending' },
  pending_approval: { label: 'Awaiting Review', cls: 'status-pending' },
  change_requested: { label: 'Changes Suggested', cls: 'status-change-requested' },
  approved: { label: 'Approved', cls: 'status-approved' },
  scheduled: { label: 'Scheduled', cls: 'status-scheduled' },
  posted: { label: 'Posted', cls: 'status-posted' },
  rejected: { label: 'Rejected', cls: 'status-rejected' },
};

const fmtDate = (value) => {
  if (!value) return null;
  try {
    return format(new Date(value), 'MMM d, yyyy');
  } catch {
    return null;
  }
};

const PostRow = ({ post, onClick }) => {
  const stage = stageOf(post);
  const meta = STAGE_META[stage] || { label: stage, cls: 'status-draft' };
  const date = stage === 'posted' ? fmtDate(post.posted_at || post.updated_at) : fmtDate(post.scheduled_date);

  return (
    <div
      onClick={onClick}
      className="p-4 rounded-xl border border-slate-100 dark:border-slate-800 hover:border-[#b8cf88] dark:hover:border-[#3d4f22] cursor-pointer transition-all"
      data-testid={`dashboard-post-${post.id}`}
    >
      <p className="text-slate-900 dark:text-white line-clamp-2 mb-2">{post.copy}</p>
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline" className={`${meta.cls} text-xs`}>{meta.label}</Badge>
        {post.post_type && (
          <Badge variant="outline" className="text-xs capitalize">
            {post.post_type.replace(/_/g, ' ')}
          </Badge>
        )}
        {date && (
          <span className="text-xs text-slate-500">{date}</span>
        )}
      </div>
    </div>
  );
};

const PostSection = ({ title, icon: Icon, posts, viewAllPath, navigate }) => {
  if (!posts.length) return null;
  return (
    <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Icon className="w-5 h-5 text-[#556B2F]" />
          {title}
          <span className="text-sm font-normal text-slate-400">({posts.length})</span>
        </CardTitle>
        {viewAllPath && posts.length > 5 && (
          <Button variant="ghost" size="sm" onClick={() => navigate(viewAllPath)}>
            View All <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {posts.slice(0, 5).map((post) => (
            <PostRow
              key={post.id}
              post={post}
              onClick={() => navigate(`/client/posts/${post.id}`)}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const StatCard = ({ icon: Icon, value, label, iconWrap, iconColor, onClick }) => (
  <Card
    onClick={onClick}
    className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 cursor-pointer hover:border-[#b8cf88] dark:hover:border-[#3d4f22] transition-colors"
  >
    <CardContent className="p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${iconWrap}`}>
        <Icon className={`w-5 h-5 ${iconColor}`} />
      </div>
      <div className="min-w-0">
        <p className="text-2xl font-bold text-slate-900 dark:text-white">{value}</p>
        <p className="text-xs text-slate-500 truncate">{label}</p>
      </div>
    </CardContent>
  </Card>
);

const ClientDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API_URL}/dashboard/client`);
      setStats(response.data.stats);
      setPosts(response.data.posts || []);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#556B2F]"></div>
      </div>
    );
  }

  const reviewPosts = posts.filter((p) =>
    ['client_review', 'pending_approval', 'change_requested'].includes(stageOf(p)),
  );
  const approvedPosts = posts.filter((p) => stageOf(p) === 'approved');
  const scheduledPosts = posts.filter((p) => stageOf(p) === 'scheduled');
  const postedPosts = posts.filter((p) => stageOf(p) === 'posted');

  const hasAny = posts.length > 0;

  return (
    <div className="space-y-6 animate-in" data-testid="client-dashboard">
      {/* Welcome Header */}
      <div className="text-center md:text-left">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white tracking-tight">
          Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}!
        </h1>
        <p className="text-slate-600 dark:text-slate-400 mt-1">
          Here's all of your content at a glance.
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard
          icon={Clock}
          value={stats?.pending_approval || 0}
          label="Awaiting Review"
          iconWrap="bg-amber-100 dark:bg-amber-900/30"
          iconColor="text-amber-600"
          onClick={() => navigate('/client/approvals')}
        />
        <StatCard
          icon={CheckCircle}
          value={stats?.approved || 0}
          label="Approved"
          iconWrap="bg-[#d8e5b8] dark:bg-[#556B2F]/15"
          iconColor="text-[#3d4f22] dark:text-[#b8cf88]"
          onClick={() => navigate('/client/approved')}
        />
        <StatCard
          icon={Calendar}
          value={stats?.scheduled || 0}
          label="Scheduled"
          iconWrap="bg-purple-100 dark:bg-purple-900/30"
          iconColor="text-purple-600"
          onClick={() => navigate('/client/calendar')}
        />
        <StatCard
          icon={Send}
          value={stats?.posted || 0}
          label="Posted"
          iconWrap="bg-green-100 dark:bg-green-900/30"
          iconColor="text-green-600"
          onClick={() => navigate('/client/approved')}
        />
      </div>

      {!hasAny ? (
        <Card className="bg-gradient-to-br from-[#f0f4e8] to-[#f0f4e8] dark:from-[#556B2F]/10 dark:to-[#3d4f22]/10 border-[#b8cf88] dark:border-[#3d4f22]">
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 bg-[#d8e5b8] dark:bg-[#556B2F]/15 rounded-full flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-[#3d4f22] dark:text-[#b8cf88]" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
              Nothing here yet
            </h3>
            <p className="text-slate-600 dark:text-slate-400">
              Your posts will appear here once your agency shares them.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <PostSection
            title="Needs Your Review"
            icon={Clock}
            posts={reviewPosts}
            viewAllPath="/client/approvals"
            navigate={navigate}
          />
          <PostSection
            title="Approved"
            icon={CheckCircle}
            posts={approvedPosts}
            viewAllPath="/client/approved"
            navigate={navigate}
          />
          <PostSection
            title="Scheduled"
            icon={Calendar}
            posts={scheduledPosts}
            viewAllPath="/client/calendar"
            navigate={navigate}
          />
          <PostSection
            title="Posted"
            icon={Send}
            posts={postedPosts}
            viewAllPath="/client/approved"
            navigate={navigate}
          />
        </>
      )}

      {/* Quick Action */}
      {(stats?.pending_approval || 0) > 0 && (
        <Button
          onClick={() => navigate('/client/approvals')}
          className="w-full h-14 bg-[#556B2F] hover:bg-[#3d4f22] text-white text-lg font-medium"
          data-testid="go-to-approvals-btn"
        >
          <FileText className="w-5 h-5 mr-2" />
          Review Posts ({stats?.pending_approval || 0})
        </Button>
      )}
    </div>
  );
};

export default ClientDashboard;
